export function syncPrestigeCartHeader(cart, root) {
  if (!cart || !Number.isSafeInteger(cart.item_count) || cart.item_count < 0) return false;
  for (const node of root.querySelectorAll('.Header__CartCount')) node.textContent = String(cart.item_count);
  for (const node of root.querySelectorAll('.Header__CartDot')) node.classList.toggle('is-visible', cart.item_count > 0);
  return true;
}

export function validQuantity(value, existing = 0, rule = {}) {
  const quantity = Number(value), total = quantity + existing;
  return String(value).trim() !== '' && Number.isSafeInteger(quantity) && quantity > 0 && Number.isSafeInteger(total) && total >= (rule.min || 1) && total % (rule.increment || 1) === 0 && (rule.max == null || total <= rule.max);
}
export function allowedTheme(theme, expected, collectionId) {
  return String(expected) === '160051790016' && String(theme?.id) === '160051790016' && ['unpublished', 'main'].includes(theme?.role) && String(collectionId) === '298476830912';
}
export function createCartClient({ root, origin, fetcher }) {
  const base = new URL(root, origin);
  if (!root || base.origin !== origin || !base.pathname.endsWith('/') || base.search || base.hash) throw Error('disabled');
  let pending = false;
  async function request(path, options = {}) {
    const controller = new AbortController(), timer = setTimeout(() => controller.abort(), 15000);
    try { return await fetcher(new URL(path, base).href, { credentials: 'same-origin', cache: 'no-store', ...options, signal: controller.signal }); }
    finally { clearTimeout(timer); }
  }
  async function read(path) { const r = await request(path); if (!r.ok) throw Error('network'); return r.json(); }
  async function reconcile() { try { return await read('cart.js'); } catch { return null; } }
  return {
    readCart: () => read('cart.js'),
    async add({ handle, id, quantity, price, currency, rule: renderedRule }) {
      if (pending) throw Error('busy');
      if (!id) throw Error('choose');
      if (!validQuantity(quantity)) throw Error('quantity_error');
      pending = true;
      try {
        let before, product;
        try { [before, product] = await Promise.all([read('cart.js'), read('products/' + encodeURIComponent(handle) + '.js')]); }
        catch { throw Error('network'); }
        if (before.currency !== currency) return { status: 'currency_changed', cart: before };
        const variant = product.variants.find(v => String(v.id) === String(id));
        if (!variant?.available) return { status: 'sold_out', variant };
        if (product.requires_selling_plan || variant.requires_selling_plan) return { status: 'selling_plan' };
        if (variant.price !== price) return { status: 'price_changed', variant };
        const existing = before.items.filter(i => String(i.variant_id) === String(id)).reduce((n, i) => n + i.quantity, 0);
        const rule = variant.quantity_rule || renderedRule || {};
        if (!validQuantity(quantity, existing, rule)) return { status: 'quantity_error' };
        let result, response;
        try {
          response = await request('cart/add.js', { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ items: [{ id: variant.id, quantity: Number(quantity) }] }) });
          result = await response.json();
        } catch { return { status: 'unknown', cart: await reconcile() }; }
        const cart = await reconcile();
        if (!response.ok) return { status: cart ? 'rejected' : 'unknown', cart, description: result.description };
        return { status: cart ? 'added' : 'unknown', cart };
      } finally { pending = false; }
    }
  };
}
export function sectionPageURL(current, next, sectionId) {
  const url = new URL(current), destination = new URL(next, current);
  if (url.origin !== destination.origin || url.pathname !== destination.pathname) throw Error('load_failed');
  for (const [key, value] of destination.searchParams) url.searchParams.set(key, value);
  url.searchParams.set('section_id', sectionId);
  return url.href;
}
if (typeof customElements !== 'undefined' && !customElements.get('mori-large-quickshop')) {
  customElements.define('mori-large-quickshop', class extends HTMLElement {
    connectedCallback() {
      if (this.ready) return;
      this.ready = true;
      this.strings = Object.fromEntries([...this.querySelectorAll('[data-key]')].map(el => [el.dataset.key, el.textContent]));
      this.grid = this.querySelector('[data-grid]');
      this.currency = this.dataset.currency;
      this.next = this.dataset.next;
      if (Number(this.dataset.page) > 1) { const first = new URL(location.href); first.searchParams.set(this.dataset.pageParam || 'page', '1'); this.next = first.href; }
      this.halted = !allowedTheme(window.Shopify?.theme, this.dataset.themeId, this.dataset.collectionId);
      try { this.client = createCartClient({ root: window.Shopify?.routes?.root, origin: location.origin, fetcher: window.fetch.bind(window) }); }
      catch { this.halted = true; }
      this.addEventListener('change', event => {
        if (event.target.matches('[data-variant]')) this.updateCard(event.target.closest('[data-card]'));
        if (event.target.matches('[data-filter],[data-available]')) this.filter();
      });
      this.addEventListener('input', event => { if (event.target.matches('[data-search]')) this.filter(); });
      this.addEventListener('click', event => {
        const button = event.target.closest('button');
        if (!button) return;
        if (button.matches('[data-add]')) this.add(button.closest('[data-card]'));
        if (button.matches('[data-retry]')) this.loadRemaining();
        if (button.matches('[data-reset]')) { this.querySelector('[data-search]').value = ''; this.querySelector('[data-filter]').value = ''; this.querySelector('[data-available]').checked = false; this.filter(); }
      });
      this.initCards();
      this.loadRemaining();
      if (this.client) this.client.readCart().then(cart => this.showCart(cart)).catch(() => this.warn('cart_failed'));
      if (this.halted) this.warn('disabled');
      this.focusHandler = () => { if (this.client) this.client.readCart().then(cart => this.showCart(cart)).catch(() => this.warn('cart_failed')); };
      window.addEventListener('focus', this.focusHandler);
    }
    disconnectedCallback() { window.removeEventListener('focus', this.focusHandler); }
    text(key) { return this.strings[key] || key; }
    money(price, currency = this.currency) { return new Intl.NumberFormat(document.documentElement.lang || 'en', { style: 'currency', currency, currencyDisplay: 'code' }).format(price / 100); }
    warn(key) { this.querySelector('[data-cart-warning]').textContent = this.text(key); }
    initCards() {
      for (const card of this.querySelectorAll('[data-card]')) {
        if (card.dataset.ready) continue;
        card.dataset.ready = 'true';
        const image = card.querySelector('img');
        if (image) { image.dataset.original = image.src; image.dataset.originalSrcset = image.srcset; }
        const title = card.querySelector('[data-title]');
        title.textContent = title.textContent.replace(/[｜|]\s*lot\s+[\w-]+/ig, '').trim();
        card.dataset.searchText = (title.textContent + ' ' + [...card.querySelector('[data-variant]').options].slice(1).map(o => o.textContent).join(' ')).toLocaleLowerCase();
        this.updateCard(card);
      }
    }
    updateCard(card) {
      const option = card.querySelector('[data-variant]').selectedOptions[0];
      const quantity = card.querySelector('[data-quantity]');
      const selected = !!option.value;
      const available = option.dataset.available === 'true';
      card.querySelector('[data-add]').disabled = this.halted || !selected || !available || option.dataset.sellingPlan === 'true';
      if (selected) {
        card.querySelector('[data-price]').textContent = option.dataset.money;
        card.querySelector('[data-stock]').textContent = this.text(available ? 'in_stock' : 'sold_out');
        quantity.min = option.dataset.min || '1'; quantity.step = option.dataset.step || '1';
        if (option.dataset.max) quantity.max = option.dataset.max; else quantity.removeAttribute('max');
        if (option.dataset.sellingPlan === 'true') card.querySelector('[data-status]').textContent = this.text('selling_plan');
      } else card.querySelector('[data-stock]').textContent = this.text('choose');
      const image = card.querySelector('img');
      if (image) {
        if (selected && option.dataset.image) { image.removeAttribute('srcset'); image.src = option.dataset.image; }
        else { image.src = image.dataset.original; image.srcset = image.dataset.originalSrcset; }
        image.alt = card.querySelector('[data-title]').textContent + (selected ? ' / ' + option.textContent : '');
      }
      card.querySelector('[data-image-note]').textContent = this.text(selected && option.dataset.image ? 'variant_photo' : 'representative');
    }
    filter() {
      const search = this.querySelector('[data-search]').value.trim().toLocaleLowerCase();
      const tag = this.querySelector('[data-filter]').value, onlyAvailable = this.querySelector('[data-available]').checked;
      let visible = 0;
      for (const card of this.querySelectorAll('[data-card]')) {
        card.hidden = !card.dataset.searchText.includes(search) || (tag && !card.dataset.tags.split('|').includes(tag)) || (onlyAvailable && card.dataset.stockAvailable !== 'true');
        if (!card.hidden) visible++;
      }
      this.querySelector('[data-count]').textContent = `${visible} / ${this.grid.children.length}`;
      this.querySelector('[data-empty]').hidden = visible !== 0;
    }
    async loadRemaining() {
      if (this.loading) return;
      this.loading = true;
      this.querySelector('[data-retry]').hidden = true;
      this.querySelector('[data-load-status]').textContent = this.text('loading');
      try {
        let pages = 0;
        while (this.next) {
          if (++pages > 20) throw Error('load_failed');
          const url = sectionPageURL(location.href, this.next, this.dataset.sectionId);
          const response = await fetch(url, { credentials: 'same-origin', cache: 'no-store', signal: AbortSignal.timeout(15000) });
          if (!response.ok) throw Error('load_failed');
          const doc = new DOMParser().parseFromString(await response.text(), 'text/html');
          const section = doc.querySelector('mori-large-quickshop');
          if (!section || section.dataset.sectionId !== this.dataset.sectionId || section.dataset.collectionId !== this.dataset.collectionId || section.dataset.themeId !== this.dataset.themeId || section.dataset.currency !== this.currency) throw Error('load_failed');
          const known = new Set([...this.grid.children].map(c => c.dataset.id));
          const cards = [...section.querySelectorAll('[data-card]')].filter(c => !known.has(c.dataset.id));
          if (!cards.length && this.grid.children.length < Number(this.dataset.total)) throw Error('load_failed');
          this.grid.append(...cards);
          this.next = section.dataset.next;
          this.initCards();
          if (this.grid.children.length === Number(this.dataset.total)) this.next = '';
        }
        if (this.grid.children.length !== Number(this.dataset.total)) throw Error('load_failed');
        for (const control of this.querySelectorAll('[data-search],[data-filter],[data-available],[data-reset]')) control.disabled = false;
        this.querySelector('[data-load-status]').textContent = this.text('loaded') + ` ${this.grid.children.length} / ${this.dataset.total}`;
      } catch {
        this.querySelector('[data-load-status]').textContent = this.text('load_failed') + ` ${this.grid.children.length} / ${this.dataset.total}`;
        this.querySelector('[data-retry]').hidden = false;
      } finally { this.loading = false; this.filter(); }
    }
    showCart(cart) {
      if (!syncPrestigeCartHeader(cart, document)) { this.warn('cart_failed'); return; }
      this.querySelector('[data-cart-summary]').textContent = `${cart.item_count} · ${this.money(cart.total_price, cart.currency)}`;
      if (cart.currency !== this.currency) { this.halted = true; this.warn('currency_changed'); for (const card of this.querySelectorAll('[data-card]')) this.updateCard(card); }
    }
    async add(card) {
      if (this.halted) { this.warn('disabled'); return; }
      if (this.processing) { card.querySelector('[data-status]').textContent = this.text('busy'); return; }
      const select = card.querySelector('[data-variant]'), option = select.selectedOptions[0], quantity = card.querySelector('[data-quantity]'), button = card.querySelector('[data-add]'), status = card.querySelector('[data-status]');
      if (!option.value) { status.textContent = this.text('choose'); return; }
      if (!validQuantity(quantity.value)) { status.textContent = this.text('quantity_error'); quantity.focus(); return; }
      this.processing = true;
      const scroll = { x: scrollX, y: scrollY }, watch = new AbortController(); let moved = false;
      for (const type of ['wheel', 'touchstart', 'keydown']) window.addEventListener(type, () => { moved = true; }, { passive: true, signal: watch.signal });
      select.disabled = quantity.disabled = button.disabled = true; button.textContent = this.text('adding');
      try {
        const result = await this.client.add({ handle: card.dataset.handle, id: option.value, quantity: Number(quantity.value), price: Number(option.dataset.price), currency: this.currency, rule: { min: Number(option.dataset.min || 1), increment: Number(option.dataset.step || 1), max: option.dataset.max ? Number(option.dataset.max) : null } });
        if (result.cart) this.showCart(result.cart);
        if (result.status === 'price_changed') { option.dataset.price = String(result.variant.price); option.dataset.money = this.money(result.variant.price); }
        if (result.status === 'sold_out') { option.dataset.available = 'false'; card.dataset.stockAvailable = [...select.options].some(o => o.dataset.available === 'true') ? 'true' : 'false'; }
        if (['unknown', 'currency_changed'].includes(result.status)) { this.halted = true; this.warn(result.status); }
        status.textContent = this.text(result.status);
        if (result.status === 'added' || result.status === 'rejected') document.dispatchEvent(new CustomEvent('cart:refresh'));
      } catch (error) { status.textContent = this.text(error.message); }
      finally {
        this.processing = false;
        select.disabled = quantity.disabled = false; button.textContent = this.text('add');
        for (const item of this.querySelectorAll('[data-card]')) this.updateCard(item);
        watch.abort();
        if (!moved) { if (!button.disabled) button.focus({ preventScroll: true }); else select.focus({ preventScroll: true }); window.scrollTo(scroll.x, scroll.y); }
      }
    }
  });
}
